import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { api, fmtMoney, fmtPct } from "@/lib/api";
import { Card } from "@/components/ds/Card";
import { PageShell, PageHeader, Section, Loading } from "@/components/ds/PageShell";
import { Stat } from "@/components/ds/Stat";
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";

export default function GroupView() {
    const [group, setGroup] = useState(null);
    const nav = useNavigate();

    useEffect(() => {
        api.get("/group/dashboard").then((r) => setGroup(r.data));
    }, []);

    if (!group)
        return (
            <PageShell>
                <Loading label="Loading the group view…" testId="group-view-loading" />
            </PageShell>
        );

    const s = group.snapshot;
    const revBarData = s.per_entity.map((p) => ({
        name: `E${p.entity_number}`,
        Revenue: p.snapshot.revenue_mtd,
        Expenses: p.snapshot.expenses_mtd,
        NetProfit: p.snapshot.net_profit_mtd,
    }));

    // Rank entities by net profit MTD for the "needs attention" lens
    const ranked = [...s.per_entity].sort(
        (a, b) => a.snapshot.net_profit_mtd - b.snapshot.net_profit_mtd,
    );
    const worst = ranked[0];
    const best = ranked[ranked.length - 1];

    return (
        <PageShell width="default" testId="group-view-page">
            <PageHeader
                eyebrow="All Crane Sales — Consolidated"
                title="Group View"
                subtitle="Side-by-side comparison across the six MYOB company files. Each entity stays logically separated; numbers are only aggregated for the headline figures below."
            />

            {/* Executive summary — who needs attention and why */}
            <Card variant="default" padding="lg" className="mb-6" testId="group-summary">
                <div className="label-soft mb-2 text-stone-500">Executive summary</div>
                <div className="text-display-md text-stone-900 leading-snug mb-3">
                    {best && worst && best.entity_id !== worst.entity_id ? (
                        <>
                            <span className="text-emerald-700">{best.display_name}</span> is carrying
                            the group; <span className="text-red-700">{worst.display_name}</span> is
                            dragging it.
                        </>
                    ) : (
                        "Group is broadly aligned across entities this month."
                    )}
                </div>
                {best && worst && (
                    <p className="text-body text-stone-500 max-w-prose">
                        {best.display_name} contributed{" "}
                        <span className="font-mono-num text-stone-700">
                            {fmtMoney(best.snapshot.net_profit_mtd)}
                        </span>{" "}
                        net profit MTD; {worst.display_name} is at{" "}
                        <span className="font-mono-num text-stone-700">
                            {fmtMoney(worst.snapshot.net_profit_mtd)}
                        </span>
                        . The variance between them accounts for most of the group result.
                    </p>
                )}

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-8 gap-y-5 mt-6">
                    <Stat label="Total cash" value={s.total_cash} size="sm" testId="kpi-total-cash" />
                    <Stat label="Revenue MTD" value={s.total_revenue_mtd} size="sm" testId="kpi-total-rev" />
                    <Stat label="Expenses MTD" value={s.total_expenses_mtd} size="sm" testId="kpi-total-exp" />
                    <Stat label="Net profit MTD" value={s.total_net_profit_mtd} size="sm" tone={s.total_net_profit_mtd >= 0 ? "positive" : "negative"} testId="kpi-total-np" />
                    <Stat label="Receivables" value={s.total_ar} size="sm" testId="kpi-total-ar" />
                    <Stat label="Payables" value={s.total_ap} size="sm" testId="kpi-total-ap" />
                </div>
            </Card>

            <Section
                title="Composition"
                subtitle="Revenue, expenses and net profit by entity."
            >
                <Card variant="default" padding="lg" testId="entity-bar-chart">
                    <div style={{ width: "100%", height: 280, minHeight: 280 }}>
                        <ResponsiveContainer>
                            <BarChart data={revBarData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                                <XAxis dataKey="name" stroke="#78716c" fontSize={11} />
                                <YAxis stroke="#78716c" fontSize={11} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                                <Tooltip formatter={(v) => fmtMoney(v)} contentStyle={{ borderRadius: 6, fontSize: 12 }} />
                                <Bar dataKey="Revenue" fill="#44403c" radius={[2, 2, 0, 0]} />
                                <Bar dataKey="Expenses" fill="#a8a29e" radius={[2, 2, 0, 0]} />
                                <Bar dataKey="NetProfit" fill="#a12a65" radius={[2, 2, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </Card>
            </Section>

            <Section
                title="Per-entity numbers"
                subtitle="Click any row to open that entity."
            >
                <Card variant="default" padding="none" testId="entity-table" className="overflow-hidden">
                    <table className="w-full text-[14px]">
                        <thead>
                            <tr className="text-left border-b border-stone-100 bg-stone-50/40">
                                <th className="py-3 px-5 label-soft text-stone-500 font-medium">Entity</th>
                                <th className="py-3 px-3 label-soft text-stone-500 font-medium text-right">Cash</th>
                                <th className="py-3 px-3 label-soft text-stone-500 font-medium text-right">Rev MTD</th>
                                <th className="py-3 px-3 label-soft text-stone-500 font-medium text-right">NP MTD</th>
                                <th className="py-3 px-3 label-soft text-stone-500 font-medium text-right">Margin</th>
                                <th className="py-3 px-5 label-soft text-stone-500 font-medium" />
                            </tr>
                        </thead>
                        <tbody>
                            {s.per_entity.map((p) => (
                                <tr
                                    key={p.entity_id}
                                    className="border-b border-stone-100 last:border-0 hover:bg-stone-50/50 cursor-pointer transition group"
                                    onClick={() => nav(`/entities/${p.entity_id}`)}
                                    data-testid={`gv-row-${p.entity_number}`}
                                >
                                    <td className="py-3.5 px-5 text-stone-800">{p.display_name}</td>
                                    <td className="py-3.5 px-3 font-mono-num text-right text-stone-700">
                                        {fmtMoney(p.snapshot.cash_position)}
                                    </td>
                                    <td className="py-3.5 px-3 font-mono-num text-right text-stone-700">
                                        {fmtMoney(p.snapshot.revenue_mtd)}
                                    </td>
                                    <td className={`py-3.5 px-3 font-mono-num text-right ${p.snapshot.net_profit_mtd >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                                        {fmtMoney(p.snapshot.net_profit_mtd)}
                                    </td>
                                    <td className="py-3.5 px-3 font-mono-num text-right text-stone-700">
                                        {fmtPct(p.snapshot.gross_margin_mtd_pct)}
                                    </td>
                                    <td className="py-3.5 px-5 text-right">
                                        <ArrowUpRight
                                            size={14}
                                            strokeWidth={2}
                                            className="text-stone-300 group-hover:text-stone-700 transition inline"
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Card>
            </Section>
        </PageShell>
    );
}
